import { useState, useEffect, useCallback } from 'react'
import type { School } from './use-schools'

interface RecentSchool {
  id: string
  name: string
  accessedAt: string
}

const RECENT_SCHOOLS_KEY = 'recent-schools'
const MAX_RECENT_SCHOOLS = 5

export function useRecentSchools() {
  const [recentSchools, setRecentSchools] = useState<RecentSchool[]>([])
  const [isMounted, setIsMounted] = useState(false)

  // Load recent schools from localStorage on mount
  useEffect(() => {
    setIsMounted(true)
    try {
      const stored = localStorage.getItem(RECENT_SCHOOLS_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as RecentSchool[]
        // Sort by accessedAt descending (most recent first)
        const sorted = parsed.sort((a, b) => new Date(b.accessedAt).getTime() - new Date(a.accessedAt).getTime())
        setRecentSchools(sorted.slice(0, MAX_RECENT_SCHOOLS))
      }
    } catch (error) {
      console.warn('Failed to load recent schools from localStorage:', error)
      setRecentSchools([])
    }
  }, [])

  // Add a school to recent schools
  const addToRecentSchools = useCallback(
    (school: Pick<School, 'id' | 'name'>) => {
      if (!isMounted) return

      setRecentSchools(current => {
        // Remove existing entry if it exists
        const filtered = current.filter(item => item.id !== school.id)

        // Add new entry at the beginning
        const updated = [
          {
            id: school.id,
            name: school.name,
            accessedAt: new Date().toISOString()
          },
          ...filtered
        ].slice(0, MAX_RECENT_SCHOOLS) // Keep only the most recent MAX_RECENT_SCHOOLS

        // Save to localStorage
        try {
          localStorage.setItem(RECENT_SCHOOLS_KEY, JSON.stringify(updated))
        } catch (error) {
          console.warn('Failed to save recent schools to localStorage:', error)
        }

        return updated
      })
    },
    [isMounted]
  )

  // Clear all recent schools
  const clearRecentSchools = useCallback(() => {
    if (!isMounted) return

    setRecentSchools([])
    try {
      localStorage.removeItem(RECENT_SCHOOLS_KEY)
    } catch (error) {
      console.warn('Failed to clear recent schools from localStorage:', error)
    }
  }, [isMounted])

  return {
    recentSchools,
    addToRecentSchools,
    clearRecentSchools,
    isReady: isMounted
  }
}
